import type { CategoryAggregate } from './categoryAggregates';
import {
  aggregateCategoryForums,
  formatCategoryLastUpdate,
  latestForumUpdateIso,
} from './categoryAggregates';
import { formatChangePercent } from './forumStatChange';
import type { ForumBase } from '../types/forum';

function formatCount(n: number): string {
  return n.toLocaleString('tr-TR');
}

function growthSentence(label: string, current: number, previous: number): string {
  const pct = formatChangePercent(current, previous);
  if (!pct) return `${label} için önceki dönem karşılaştırması henüz yok`;
  const diff = current - previous;
  const direction = diff >= 0 ? 'artış' : 'azalış';
  return `${label} geçen döneme göre ${pct} ${direction} (${formatCount(Math.abs(diff))} ${diff >= 0 ? 'fazla' : 'eksik'})`;
}

/** Tek yazılım sayfası (ör. XenForo) için SEO özeti */
export function buildSoftwareSeoParagraph(
  softwareName: string,
  agg: CategoryAggregate,
  lastUpdateIso: string | null,
  softwareDescription?: string | null,
  marketSharePercent?: number | null
): string {
  const intro =
    softwareDescription?.trim() ||
    `${softwareName} forum yazılımını kullanan Türk forum sitelerinin güncel istatistikleri ve karşılaştırmalı analizi.`;

  const sharePart =
    marketSharePercent != null && marketSharePercent > 0
      ? `Listelenen aktif forumların yaklaşık %${marketSharePercent.toFixed(1)}'i ${softwareName} kullanıyor. `
      : '';

  const lastPart = lastUpdateIso
    ? `Veriler en son ${formatCategoryLastUpdate(lastUpdateIso)} tarihinde güncellendi.`
    : 'Veri güncelleme tarihi henüz kayıtlı değil.';

  const totals = `${softwareName} ile çalışan ${agg.forumCount} forumda toplam ${formatCount(agg.totalMembers)} üye, ${formatCount(agg.totalTopics)} konu ve ${formatCount(agg.totalPosts)} ileti kayıtlı.`;

  const growth = [
    growthSentence('Toplam üye', agg.totalMembers, agg.previousTotalMembers),
    growthSentence('Toplam konu', agg.totalTopics, agg.previousTotalTopics),
    growthSentence('Toplam ileti', agg.totalPosts, agg.previousTotalPosts),
  ].join('. ');

  return `${intro} ${sharePart}${totals} ${lastPart} ${growth}.`;
}

/** /software liste sayfası için genel özet */
export function buildSoftwareListSeoParagraph(
  softwareCount: number,
  agg: CategoryAggregate,
  lastUpdateIso: string | null
): string {
  const lastPart = lastUpdateIso
    ? `Son veri güncellemesi: ${formatCategoryLastUpdate(lastUpdateIso)}.`
    : '';

  const totals = `Türk forumlarında ${softwareCount} farklı yazılım tespit edildi; toplam ${formatCount(agg.forumCount)} aktif forum, ${formatCount(agg.totalMembers)} üye, ${formatCount(agg.totalTopics)} konu ve ${formatCount(agg.totalPosts)} ileti istatistiği tutuluyor.`;

  const growth = [
    growthSentence('Toplam üye', agg.totalMembers, agg.previousTotalMembers),
    growthSentence('Toplam konu', agg.totalTopics, agg.previousTotalTopics),
    growthSentence('Toplam ileti', agg.totalPosts, agg.previousTotalPosts),
  ].join('. ');

  return `Forum yazılımları pazar payı ve büyüme karşılaştırması. ${totals} ${lastPart} ${growth}.`;
}

export function getSoftwareMemberGrowth(forums: ForumBase[]): number | null {
  const agg = aggregateCategoryForums(forums);
  if (agg.previousTotalMembers <= 0) return null;
  const pct = ((agg.totalMembers - agg.previousTotalMembers) / agg.previousTotalMembers) * 100;
  return pct > 0 ? pct : null;
}

export { latestForumUpdateIso, formatCategoryLastUpdate };
